/**
 * PATH: packages/email-hub/src/feature/lib/kanbanDeepLink.ts
 * FILE_ID: EMAIL-KANBAN-LIB-001
 * PURPOSE: Build the canonical Kanban deep-link from an email's allocation
 *          summary so "Open card" / "Open on board" lands on the RIGHT board
 *          AND opens the card drawer.
 * WHY:     The email side historically built `/kanban?card=<id>` with no board
 *          id. The Kanban page reads the board from the `:boardId` path param
 *          and the card from `?card=`; with no board it mounts the home/last
 *          board, so `?card=` only matched if the card happened to live there —
 *          cross-board cards silently failed to open. The correct shape (used
 *          by the kanban search palette) is `/kanban/<boardId>?card=<cardId>`.
 * FLOW: { board_id, card_id } → '/kanban/<board>?card=<card>'  (pure I→O)
 */

/** Minimal shape — kept structural to avoid a circular import on the full
 *  EmailAllocationSummary type (which lives in @quotech/tenant-shared). */
export interface KanbanLinkSource {
  board_id?: string | null
  card_id?: string | null
}

/**
 * Returns the deep-link to open the allocated card on its board, or null when
 * the email isn't allocated to a card.
 *
 * - board + card → `/kanban/<board_id>?card=<card_id>` (opens board + drawer)
 * - card only    → `/kanban?card=<card_id>` (legacy fallback; opens drawer iff
 *                   the card is on the mounted board)
 * - neither      → null (caller hides the CTA)
 */
export function buildKanbanCardLink(
  summary: KanbanLinkSource | null | undefined,
): string | null {
  const cardId = summary?.card_id
  if (!cardId) return null
  const boardId = summary?.board_id
  if (boardId) {
    return `/kanban/${encodeURIComponent(boardId)}?card=${encodeURIComponent(cardId)}`
  }
  return `/kanban?card=${encodeURIComponent(cardId)}`
}

/**
 * "Go to board" deep-link — opens the BOARD the email is allocated to,
 * popping the card drawer too when there's a card id. Unlike
 * buildKanbanCardLink (card-first; null when there's no card), this is
 * board-first: a pending/private AI suggestion that has a board but no
 * card id yet still lands you on the right board.
 *
 * - board + card → `/kanban/<board_id>?card=<card_id>` (board + drawer)
 * - board only   → `/kanban/<board_id>` (just the board)
 * - neither      → null (caller hides the affordance)
 */
export function buildKanbanBoardLink(
  summary: KanbanLinkSource | null | undefined,
): string | null {
  const boardId = summary?.board_id
  if (!boardId) return null
  const cardId = summary?.card_id
  return cardId
    ? `/kanban/${encodeURIComponent(boardId)}?card=${encodeURIComponent(cardId)}`
    : `/kanban/${encodeURIComponent(boardId)}`
}
